// src/components/dashboard/WebsiteLeadLaneChart.tsx
//
// Native HTML/CSS bar chart for "Incoming Leads" on the dashboard.
//
// Replaces a Recharts <BarChart> whose hover-cursor (visual layer) and
// Bar/background (click target) were different SVG layers with inconsistent
// event behavior that shifted whenever the sidebar opened. Every patch made
// one state worse — see the prior commit ladder on this branch for the
// receipts.
//
// Design: each bucket is rendered as a flex-1 <button> "lane" that owns
// BOTH the hover highlight AND the click hitbox as the same DOM element.
// 7D / 30D / 90D all use the same code path. Drilldown logic (90D week →
// daily) stays in the parent — this component only renders whatever data
// is passed in, so swapping between range and drilldown is just a re-render.

import React from "react";
import { Box, Tooltip, Typography, alpha, useTheme } from "@mui/material";
import { type WeeklyLeadBucket } from "../../lib/dashboardLeads";

const SELECTED_FILL = "#8B5CF6";
const Y_AXIS_WIDTH_PX = 28;

interface WebsiteLeadLaneChartProps {
  data: WeeklyLeadBucket[];
  selectedBucket: WeeklyLeadBucket | null;
  onSelectBucket: (bucket: WeeklyLeadBucket) => void;
  /** Optional formatter for the tooltip title row. Falls back to the
   * bucket's `date` label if not provided. */
  formatBucketTitle?: (bucket: WeeklyLeadBucket) => string;
}

// Show every Nth label on the x-axis. Stride is anchored from the END
// (rightmost / most recent date) so the last label always shows AND
// the stride is uniform — no "force last" override that can crowd
// against the previous-shown label when (total - 1) isn't a multiple
// of step (the May-13 / May-15 30D collision was this exact bug).
// Threshold of 15 lets 90D's 13 weekly buckets all show.
const shouldShowLabel = (index: number, total: number): boolean => {
  if (total <= 15) return true;
  const step = Math.ceil(total / 12);
  return (total - 1 - index) % step === 0;
};

// Round a max value up to a slightly larger "nice" number so the tallest
// bar doesn't touch the chart ceiling.
const niceCeil = (n: number): number => {
  if (n <= 1) return 1;
  if (n <= 5) return n + 1;
  if (n <= 10) return Math.ceil(n / 2) * 2;
  return Math.ceil(n * 1.1);
};

// Axis scale where every tick is an INTEGER and every gridline lines up
// with real values. Small maxima use one tick per unit (yMax 3 → 3/2/1/0);
// larger maxima use 4 ticks with yMax padded to a multiple of 4. This kills
// the duplicate-label bug (3, 2, 2, 1, 0 from rounding fractional ticks)
// AND makes bar heights land exactly on their gridlines.
const buildYScale = (maxCount: number): { yMax: number; ticks: number } => {
  const rough = niceCeil(maxCount);
  if (rough <= 4) return { yMax: rough, ticks: Math.max(rough, 1) };
  const ticks = 4;
  return { yMax: Math.ceil(rough / ticks) * ticks, ticks };
};

const WebsiteLeadLaneChart: React.FC<WebsiteLeadLaneChartProps> = ({
  data,
  selectedBucket,
  onSelectBucket,
  formatBucketTitle,
}) => {
  const theme = useTheme();
  const maxCount = data.reduce((max, d) => (d.count > max ? d.count : max), 0);
  const { yMax, ticks: yTicks } = buildYScale(maxCount);
  const hasSelection = !!selectedBucket;

  if (data.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Plot area: y-axis labels + lane row */}
      <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
        {/* Y-axis tick labels */}
        <Box
          sx={{
            width: Y_AXIS_WIDTH_PX,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "flex-end",
            pr: 0.5,
            py: "2px",
          }}
        >
          {Array.from({ length: yTicks + 1 }).map((_, i) => {
            const value = Math.round(yMax * (1 - i / yTicks));
            return (
              <Typography
                key={i}
                variant="caption"
                sx={{
                  color: theme.palette.text.primary,
                  fontSize: 11,
                  lineHeight: 1,
                  opacity: 0.7,
                }}
              >
                {value}
              </Typography>
            );
          })}
        </Box>

        {/* Lane row */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "stretch",
            gap: "2px",
            position: "relative",
            // Subtle horizontal gridlines at each y-axis tick
            backgroundImage: `repeating-linear-gradient(
              to bottom,
              transparent 0,
              transparent calc(${100 / yTicks}% - 1px),
              ${alpha(theme.palette.text.primary, 0.06)} calc(${100 / yTicks}% - 1px),
              ${alpha(theme.palette.text.primary, 0.06)} ${100 / yTicks}%
            )`,
          }}
        >
          {data.map((bucket) => {
            const isSelected =
              selectedBucket?.isoDate === bucket.isoDate &&
              selectedBucket?.endIsoDate === bucket.endIsoDate;
            const isDimmed = hasSelection && !isSelected;
            const heightPct = yMax > 0 ? (bucket.count / yMax) * 100 : 0;
            const bucketTitle = formatBucketTitle
              ? formatBucketTitle(bucket)
              : bucket.date;

            return (
              <Tooltip
                key={`${bucket.isoDate}-${bucket.endIsoDate}`}
                title={
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: "#94a3b8", display: "block" }}
                    >
                      {bucketTitle}
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{ color: "#e2e8f0" }}
                    >
                      {bucket.count} website lead{bucket.count === 1 ? "" : "s"}
                    </Typography>
                  </Box>
                }
                placement="top"
                arrow
                enterDelay={150}
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: "#0f172a",
                      border: "1px solid rgba(148, 163, 184, 0.25)",
                      boxShadow: 8,
                      px: 1.25,
                      py: 1,
                    },
                  },
                  arrow: { sx: { color: "#0f172a" } },
                }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={() => onSelectBucket(bucket)}
                  aria-label={`${bucketTitle}: ${bucket.count} ${
                    bucket.count === 1 ? "lead" : "leads"
                  }`}
                  aria-pressed={isSelected}
                  sx={{
                    flex: 1,
                    minWidth: 6,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    position: "relative",
                    border: "none",
                    padding: 0,
                    borderRadius: "8px",
                    background: isSelected
                      ? alpha(SELECTED_FILL, 0.1)
                      : "transparent",
                    cursor: "pointer",
                    transition: "background-color 120ms",
                    "&:hover": {
                      backgroundColor: isSelected
                        ? alpha(SELECTED_FILL, 0.16)
                        : alpha(theme.palette.primary.main, 0.08),
                    },
                    "&:focus-visible": {
                      outline: `2px solid ${alpha(
                        theme.palette.primary.main,
                        0.6
                      )}`,
                      outlineOffset: "-2px",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: "70%",
                      maxWidth: 32,
                      height: `${heightPct}%`,
                      minHeight: bucket.count > 0 ? 2 : 0,
                      backgroundColor: isSelected
                        ? SELECTED_FILL
                        : theme.palette.primary.main,
                      opacity: bucket.count === 0 ? 0.35 : isDimmed ? 0.35 : 1,
                      borderRadius: "6px 6px 0 0",
                      transition:
                        "background-color 120ms, opacity 120ms, height 180ms",
                      pointerEvents: "none",
                    }}
                  />
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </Box>

      {/* X-axis labels */}
      <Box sx={{ display: "flex", mt: 0.5 }}>
        <Box sx={{ width: Y_AXIS_WIDTH_PX, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", gap: "2px" }}>
          {data.map((bucket, i) => (
            <Typography
              key={`label-${bucket.isoDate}-${bucket.endIsoDate}`}
              variant="caption"
              sx={{
                flex: 1,
                minWidth: 6,
                textAlign: "center",
                color: theme.palette.text.primary,
                fontSize: 11,
                opacity: 0.85,
                whiteSpace: "nowrap",
                overflow: "visible",
                textOverflow: "clip",
                pointerEvents: "none",
              }}
            >
              {shouldShowLabel(i, data.length) ? bucket.date : " "}
            </Typography>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default WebsiteLeadLaneChart;
